import { useState, useRef, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InlineEditableV10Props {
  readonly value: string;
  readonly onSave: (val: string) => void;
  readonly onCancel: () => void;
  readonly fieldType: 'title' | 'description';
}

export function InlineEditableV10({ value, onSave, onCancel, fieldType }: InlineEditableV10Props) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (fieldType === 'title' || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSave(draft);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <textarea
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        className={cn(
          "w-full rounded-lg border transition-all resize-none",
          "bg-[hsl(var(--v10-bg-elevated))] border-[hsl(var(--v10-border))]",
          "focus:border-[hsl(var(--v10-accent))] focus:ring-1 focus:ring-[hsl(var(--v10-accent)_/_0.3)]",
          "outline-none [field-sizing:content]",
          fieldType === 'title'
            ? "text-base font-semibold text-[hsl(var(--v10-text-primary))] py-2.5 px-3 min-h-[64px]"
            : "text-sm text-[hsl(var(--v10-text-secondary))] py-2 px-3 leading-relaxed min-h-[72px] max-h-[40vh] overflow-y-auto"
        )}
        rows={fieldType === 'title' ? 2 : 3}
      />
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSave(draft)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium hover:opacity-90 transition-opacity shadow-sm"
          style={{ background: 'hsl(var(--v10-status-success))' }}
        >
          <Check className="w-3.5 h-3.5" />
          Valider
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{ background: 'hsl(var(--v10-bg-hover))', color: 'hsl(var(--v10-text-secondary))' }}
        >
          <X className="w-3.5 h-3.5" />
          Annuler
        </button>
      </div>
    </div>
  );
}
